import { Injectable } from '@angular/core';
import {
    GeoJsonGeometryTypes,
    IDatiqMapGeoJson,
    IDatiqMapGeoJsonFeature,
    IIqMcMapLatLng,
    defaultDatiqMapStyleConfiguration,
    featureSelectedStyleOptions,
} from '../../models';
import { flatten, noop } from 'lodash';
import pointInPolygon from 'point-in-polygon';
import { IIqMcXmaModalData } from '@dat/iq-mc-modal';
import { mapStylesOverlay } from './models';

@Injectable({
    providedIn: 'root',
})
export class IqMcMapUtilsService {
    constructor() {}

    setGeoJsonToMap(map: google.maps.Map, geoJson: IDatiqMapGeoJson) {
        //Remove previous features so that new layers are not drawn on top of existing ones
        map.data.forEach((feature) => {
            map.data.remove(feature);
        });
        //Adding in id here for faster retreival when fetching a feature via geoCode
        geoJson.features.forEach((feature) => {
            feature.id = feature.properties.code;
        });
        map.data.addGeoJson(geoJson);
    }

    setMapStyles(map: google.maps.Map) {
        //Clear Previous Styles
        map.data.setStyle({});
        map.data.setStyle(this._setStyleForEachFeature);
    }

    highlightSelectedFeature(map: google.maps.Map, geoCode: string) {
        map.data.revertStyle();
        if (!geoCode) {
            return;
        }
        const feature: google.maps.Data.Feature =
            map.data.getFeatureById(geoCode);
        !!feature
            ? map.data.overrideStyle(feature, featureSelectedStyleOptions)
            : noop();
    }

    hightlightSelectedFeatureFromEvent(
        map: google.maps.Map,
        event: google.maps.Data.MouseEvent,
    ) {
        map.data.revertStyle();
        map.data.overrideStyle(event.feature, featureSelectedStyleOptions);
    }

    getGeometriesContaingPoint(
        event: google.maps.Data.MouseEvent,
        geometries: IDatiqMapGeoJson,
    ): IIqMcXmaModalData[] {
        const point = this._mapMouseEventToLatLong(event);
        const filteredGeometries = geometries.features.filter(
            (feature: IDatiqMapGeoJsonFeature): boolean => {
                return this._featureContainsPoint(point, feature);
            },
        );
        return this._mapContainedGeometriesToModalData(filteredGeometries);
    }

    addOverlayMap(baseMap: google.maps.Map): void {
        const labelsMapType = new google.maps.StyledMapType(
            [...mapStylesOverlay],
            {
                name: 'Labels',
            },
        );

        baseMap.overlayMapTypes.insertAt(0, labelsMapType);

        const auxOverlay = new google.maps.OverlayView();
        auxOverlay.draw = function () {};
        auxOverlay.onAdd = function () {
            const mapPane: HTMLElement = auxOverlay.getPanes()
                .mapPane as HTMLElement;
            mapPane.style.zIndex = '150';

            const markerLayer: HTMLElement = auxOverlay.getPanes()
                .markerLayer as HTMLElement;
            markerLayer.style.zIndex = '151';
        };
        auxOverlay.setMap(baseMap);
    }

    // #region Private Methods

    private _setStyleForEachFeature(
        feature: google.maps.Data.Feature,
    ): google.maps.Data.StyleOptions {
        const { fillColor } = feature.getProperty('style');
        return {
            ...defaultDatiqMapStyleConfiguration,
            fillColor: fillColor,
            zIndex: -1,
        } as google.maps.Data.StyleOptions;
    }

    private _mapMouseEventToLatLong(
        evt: google.maps.Data.MouseEvent,
    ): IIqMcMapLatLng {
        const lat: number = evt.latLng.lat();
        const lng: number = evt.latLng.lng();

        return {
            lat,
            lng,
        };
    }

    private _featureContainsPoint(
        point: IIqMcMapLatLng,
        feature: IDatiqMapGeoJsonFeature,
    ): boolean {
        const testPoint: number[] = [point.lng, point.lat];

        const coordinates: number[][][] =
            this._mapFlattenedCoordinates(feature);

        return coordinates.some((polygon: number[][]): boolean =>
            pointInPolygon(testPoint, polygon),
        );
    }

    private _mapFlattenedCoordinates(
        feature: IDatiqMapGeoJsonFeature,
    ): number[][][] {
        const coordinates: number[][][] =
            feature.geometry.type === GeoJsonGeometryTypes.MultiPolygon
                ? flatten(feature.geometry.coordinates)
                : feature.geometry.coordinates;

        return coordinates;
    }

    private _mapContainedGeometriesToModalData(
        geometries: IDatiqMapGeoJsonFeature[],
    ): IIqMcXmaModalData[] {
        return geometries.map(
            (geometry: IDatiqMapGeoJsonFeature): IIqMcXmaModalData => {
                return {
                    code: geometry.properties.code,
                    name: geometry.properties.name,
                    fillColor: geometry.properties.style.fillColor,
                };
            },
        );
    }

    // #endregion
}
